<script setup lang="ts">
import { useId } from 'vue';

type InputsProps = {
  modelValue: string | number;
  type: 'text' | 'number' | 'phone' | 'email';
  label: string;
  error?: string | null;
};

defineProps<InputsProps>();
defineEmits(['update:modelValue']);
const inputId = useId();
</script>

<template>
  <div class="base-input-container">
    <label
      :for="inputId"
      class="base-input-label"
    >
      {{ label }}
      <span v-if="!!$attrs.required" aria-hidden="true" class="required-star">*</span>
      <span v-if="error" class="base-input-error">{{ error }}</span>
    </label>

    <input
      v-bind="$attrs"
      :id="inputId"
      :type="type"
      :value="modelValue"
      :required="!!$attrs.required"
      :aria-required="!!$attrs.required"
      :aria-labelledby="inputId"
      :aria-invalid="!!$attrs['aria-invalid']"
      class="base-input"
      @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
    >
  </div>
</template>

<style scoped>
.base-input-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.base-input-label {
  font-size: 0.95rem;
  font-weight: 500;
  color: #1f2937;
  text-align: left;
  display: block;
}

.required-star {
  color: var(--yb-color-error);
  margin-left: 0.25rem;
  margin-right: 0.5rem;
  font-weight: 700;
}

.base-input-error {
  font-size: 0.85rem;
  font-weight: 500;
  color: #ef4444;
  text-align: left;
  display: block;
}

.base-input {
  display: block;
  height: 2.5rem;

  border: 1px solid #d1d5db;
  background-color: white;
  border-radius: 0.375rem;

  padding: 0 0.75rem;
  font-size: 0.875rem;
  line-height: 2.5rem;
  color: #1f2937;

  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* overwrite user-agent style on auto-completed form fields. */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-text-fill-color: var(--yb-color-black) !important;
  background-color: var(--yb-color-white) !important;
  appearance: none !important;
}

.base-input:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--yb-color-primary);
  border-color: var(--yb-color-primary);
}

.base-input:disabled {
  cursor: not-allowed;
  opacity: 0.6;
  background-color: #f3f4f6;
}

.base-input::placeholder {
  color: #9ca3af;
}
</style>
